
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ResetPasswordDialog from './ResetPasswordDialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Chrome, Mail, Lock } from 'lucide-react';

interface FormData {
  email: string;
  password: string;
}

interface LoginFormProps {
  onSubmit: (data: FormData) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSubmit, loading, error }) => {
  const [googleLoading, setGoogleLoading] = useState(false);
  const form = useForm<FormData>();

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth`
        }
      });
      
      if (error) {
        toast.error('Google login failed: ' + error.message);
      }
    } catch (err) {
      toast.error('Failed to connect with Google');
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Google Login Button - Temporarily disabled until OAuth is configured */}
      {false && (
        <Button 
          type="button"
          variant="outline"
          className="w-full h-12 text-base font-medium border-2 hover:border-green-300 transition-colors"
          onClick={handleGoogleLogin}
          disabled={googleLoading || loading}
        >
          <Chrome className="w-5 h-5 mr-3 text-red-500" />
          {googleLoading ? 'Connecting...' : 'Continue with Google'}
        </Button>
      )}

      {/* Only show separator if Google button was visible */}
      {false && (
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">or</span>
          </div>
        </div>
      )}

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        {/* Email Input - Mobile Optimized */}
        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium">
            Email Address
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              id="email"
              type="email"
              placeholder="your@email.com"
              autoComplete="email"
              className="h-12 text-base pl-10"
              {...form.register('email', { required: true })}
            />
          </div>
        </div>

        {/* Password Input - Mobile Optimized */}
        <div className="space-y-2">
          <label htmlFor="password" className="block text-sm font-medium">
            Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              id="password"
              type="password"
              placeholder="Enter your password"
              autoComplete="current-password"
              className="h-12 text-base pl-10"
              {...form.register('password', { required: true })}
            />
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-4 rounded-lg bg-red-50 border border-red-200">
            <p className="text-sm text-red-700 text-center">{error}</p>
          </div>
        )}
        
        {/* Login Button - Mobile Optimized */}
        <Button 
          type="submit" 
          className="w-full h-12 text-base font-medium bg-green-600 hover:bg-green-700 text-white" 
          disabled={loading}
        >
          {loading ? 'Signing In...' : 'Sign In'}
        </Button>
        
        {/* Reset Password Link */}
        <div className="text-center pt-2">
          <ResetPasswordDialog />
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
